import React, { useState, useRef, useEffect } from "react";
import Button from "../Button/Button";
import "./button-dropdown.scss";

export interface DropdownOption {
  id: string;
  label: string;
  description?: string;
  disabled?: boolean;
}

export interface ButtonDropdownProps {
  children: React.ReactNode;
  options: DropdownOption[];
  variant?: "primary" | "secondary";
  size?: "small" | "medium" | "large";
  loading?: boolean;
  disabled?: boolean;
  onItemClick?: (option: DropdownOption) => void;
  className?: string;
}

const ButtonDropdown: React.FC<ButtonDropdownProps> = ({
  children,
  options,
  variant = "primary",
  size = "medium",
  loading = false,
  disabled = false,
  onItemClick,
  className = "",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const [dropdownPosition, setDropdownPosition] = useState<{
    alignRight: boolean;
    showAbove: boolean;
    maxWidth?: number;
  }>({ alignRight: false, showAbove: false });
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        setFocusedIndex(-1);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [isOpen]);

  // Calculate dropdown position to prevent overflow
  useEffect(() => {
    if (isOpen && buttonRef.current && menuRef.current) {
      const calculatePosition = () => {
        const button = buttonRef.current!;
        const menu = menuRef.current!;
        const buttonRect = button.getBoundingClientRect();
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;

        // Get menu dimensions (it's rendered but might be positioned off-screen initially)
        const menuRect = menu.getBoundingClientRect();
        const menuWidth = menuRect.width || 200; // fallback width
        const menuHeight = menuRect.height || options.length * 50; // estimate height

        // Calculate space available in each direction
        const spaceRight = viewportWidth - buttonRect.right;
        const spaceLeft = buttonRect.left;
        const spaceBelow = viewportHeight - buttonRect.bottom;
        const spaceAbove = buttonRect.top;

        // Determine horizontal alignment
        let alignRight = false;
        let maxWidth: number | undefined;

        // If dropdown would overflow on the right, align to the right
        if (
          buttonRect.left + menuWidth > viewportWidth &&
          spaceLeft >= menuWidth
        ) {
          alignRight = true;
        }

        // If still not enough space, constrain width
        if (menuWidth > Math.max(spaceRight, spaceLeft)) {
          maxWidth = Math.max(spaceRight, spaceLeft) - 20; // 20px margin
        }

        // Determine vertical position
        let showAbove = false;
        if (menuHeight > spaceBelow && spaceAbove > spaceBelow) {
          showAbove = true;
        }

        setDropdownPosition({
          alignRight,
          showAbove,
          maxWidth,
        });
      };

      // Calculate position immediately and after a brief delay to ensure menu is rendered
      calculatePosition();
      const timeoutId = setTimeout(calculatePosition, 10);

      // Recalculate on window resize
      const handleResize = () => calculatePosition();
      window.addEventListener("resize", handleResize);

      return () => {
        clearTimeout(timeoutId);
        window.removeEventListener("resize", handleResize);
      };
    }
  }, [isOpen, options.length]);

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!isOpen) return;

      switch (event.key) {
        case "Escape":
          setIsOpen(false);
          setFocusedIndex(-1);
          buttonRef.current?.focus();
          break;
        case "ArrowDown":
          event.preventDefault();
          setFocusedIndex((prev) => (prev < options.length - 1 ? prev + 1 : 0));
          break;
        case "ArrowUp":
          event.preventDefault();
          setFocusedIndex((prev) => (prev > 0 ? prev - 1 : options.length - 1));
          break;
        case "Enter":
        case " ":
          event.preventDefault();
          if (focusedIndex >= 0 && !options[focusedIndex].disabled) {
            handleOptionClick(options[focusedIndex]);
          }
          break;
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleKeyDown);
      return () => document.removeEventListener("keydown", handleKeyDown);
    }
  }, [isOpen, focusedIndex, options]);

  const handleButtonClick = () => {
    if (!disabled && !loading) {
      setIsOpen(!isOpen);
      setFocusedIndex(-1);
    }
  };

  const handleOptionClick = (option: DropdownOption) => {
    if (!option.disabled && onItemClick) {
      onItemClick(option);
    }
    setIsOpen(false);
    setFocusedIndex(-1);
    buttonRef.current?.focus();
  };

  return (
    <div className="finspace-button-dropdown-wrapper" ref={dropdownRef}>
      <div ref={buttonRef} className="finspace-button-dropdown-trigger">
        <Button
          variant={variant}
          size={size}
          loading={loading}
          disabled={disabled}
          onClick={handleButtonClick}
          className={`finspace-button-dropdown-button ${
            isOpen ? "finspace-button-dropdown-button--open" : ""
          }`}
          icon="caret-down-filled"
          iconPosition="trailing"
        >
          {children}
        </Button>
      </div>

      {isOpen && (
        <div
          className={`finspace-button-dropdown__menu ${
            dropdownPosition.alignRight
              ? "finspace-button-dropdown__menu--align-right"
              : ""
          } ${
            dropdownPosition.showAbove
              ? "finspace-button-dropdown__menu--above"
              : ""
          } ${
            dropdownPosition.maxWidth
              ? "finspace-button-dropdown__menu--constrained"
              : ""
          }`}
          ref={menuRef}
          role="menu"
          aria-labelledby="dropdown-button"
          style={
            dropdownPosition.maxWidth
              ? { maxWidth: `${dropdownPosition.maxWidth}px` }
              : undefined
          }
        >
          {options.map((option, index) => (
            <div
              key={option.id}
              className={`finspace-button-dropdown__option ${
                option.disabled
                  ? "finspace-button-dropdown__option--disabled"
                  : ""
              } ${
                index === focusedIndex
                  ? "finspace-button-dropdown__option--focused"
                  : ""
              }`}
              role="menuitem"
              tabIndex={-1}
              aria-disabled={option.disabled}
              onClick={() => handleOptionClick(option)}
              onMouseEnter={() => setFocusedIndex(index)}
            >
              <div className="finspace-button-dropdown__option-content">
                <div className="finspace-button-dropdown__option-label">
                  {option.label}
                </div>
                {option.description && (
                  <div className="finspace-button-dropdown__option-description">
                    {option.description}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ButtonDropdown;
